import cx from 'classnames'
import dynamic from 'next/dynamic'
import { type ReactNode, useMemo } from 'react'
import { useElementSize } from 'usehooks-ts'

import { type SanityMarquee } from '@data/sanity/queries/types/modules'
import { backgroundColorClasses } from '@lib/color'
import { getSpacingClasses } from '@lib/spacing'

const ComplexPortableText = dynamic(
  () => import('@components/complex-portable-text')
)
const Photo = dynamic(() => import('@components/photo'))

interface MarqyProps {
  speed?: number
  direction?: 'left' | 'right'
  pauseOnHover?: boolean
  children: ReactNode
}

type MarqueeProps = SanityMarquee

interface MarqueeItemProps {
  item: SanityMarquee['items'][number]
}

const Marqy = ({
  speed = 0.5,
  direction = 'left',
  pauseOnHover,
  children,
}: MarqyProps): JSX.Element => {
  const [containerRef, containerSize] = useElementSize()
  const [itemRef, itemSize] = useElementSize()

  const itemCount = useMemo(() => {
    if (!containerSize.width || !itemSize.width) {
      return 1
    }

    return Math.ceil(containerSize.width / itemSize.width)
  }, [containerSize.width, itemSize.width])
  const animationDuration = useMemo(
    () => ((itemSize.width ?? 0) * itemCount) / (100 * speed),
    [itemCount, itemSize.width, speed]
  )

  const clones = new Array(2).fill(0)
  const items = new Array(itemCount).fill(0)

  return (
    <div
      ref={containerRef}
      data-marqy=""
      data-direction={direction}
      data-pause-on-hover={pauseOnHover ? '' : null}
    >
      <div data-marqy-inner="">
        {clones.map((_, cloneIndex) => (
          <div
            key={cloneIndex}
            data-marqy-content=""
            style={{
              animationDuration: `${animationDuration}s`,
            }}
          >
            {items.map((_, itemIndex) => (
              <div
                key={itemIndex}
                ref={cloneIndex === 0 && itemIndex === 0 ? itemRef : undefined}
                aria-hidden={cloneIndex > 0 || itemIndex > 0 || undefined}
                data-marqy-item=""
              >
                {children}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

const MarqueeItem = ({ item }: MarqueeItemProps) => {
  switch (item._type) {
    case 'simple': {
      return (
        <span className="block p-4 text-5xl leading-none">{item.text}</span>
      )
    }

    case 'photo': {
      return (
        <div className="p-4 w-[calc(80vw-2rem)] max-w-[28rem]">
          <Photo image={item.photo} />
        </div>
      )
    }

    default: {
      return null
    }
  }
}

const Marquee = ({
  content,
  contentAlignment,
  items,
  speed,
  reverse,
  pausable,
  spacing,
  background,
  reverseSequence,
}: MarqueeProps) => {
  const sectionClasses = useMemo(
    () => [
      background ? backgroundColorClasses[background] : null,
      getSpacingClasses(spacing?.padding),
    ],
    [background, spacing]
  )
  const orderedItems = useMemo(() => {
    const orderedItems = items ? [...items] : []

    return reverseSequence ? orderedItems.reverse() : orderedItems
  }, [items, reverseSequence])

  if (orderedItems.length === 0) {
    return null
  }

  return (
    <section className={cx(sectionClasses)}>
      {content && (
        <div className="container">
          <ComplexPortableText
            content={content}
            className={cx('rc rc-marquee flex flex-col', {
              'items-start text-left': contentAlignment === 'left',
              'items-center text-center': contentAlignment === 'center',
              'items-end text-right': contentAlignment === 'right',
            })}
          />
        </div>
      )}

      <Marqy
        speed={speed}
        direction={reverse ? 'right' : 'left'}
        pauseOnHover={pausable}
      >
        <div className="flex">
          {orderedItems.map((item) => (
            <MarqueeItem key={item._key} item={item} />
          ))}
        </div>
      </Marqy>
    </section>
  )
}

export default Marquee
